import { Component, defineElement, FC, onDidLoad, useProp } from '@atomify/hooks';
import { h } from '@atomify/jsx';
import { Button } from '@atoms/button/button';
import {
    BPDFormElement,
    InputElement,
    RadioElement,
    SelectElement,
    SelectOption,
    TextareaElement,
} from '@atoms/form-elements';
import { PagesProps } from '@atoms/index';
import { Col, Row } from '@bpd-library/components';
import { askAQuestion, AskAQuestionProps } from '@utilities/api-calls/ask-a-question';
import {
    translateItem,
    useButtonLoadingState,
    useStylesheet,
    useTranslate,
} from '@utilities/hooks';
import { isGermany } from '@utilities/is-germany';

import style from './message-form.scss';

type ContactReason = 'Question' | 'Complaint';

interface MessageFormInterface extends Component {
    bpdPages: PagesProps;
    contactReason: ContactReason;
}

const DEFAULT_COMPLAINT_TYPES = ['Makelaar', 'Aannemer', 'BPD', 'Website / Account', 'Overig'];
const SR_ONLY_CLASS = 'u-sr-only';

const MessageForm: FC<MessageFormInterface> = ({ element }) => {
    const [onSuccess] = useProp<Function | null>('onSuccess', null);
    const [onError] = useProp<Function | null>('onError', null);
    const [, setContactReason, watchContactReason] = useProp<ContactReason | null>(
        'contactReason',
        null,
    );
    const [complaintTypes] = useProp<string[]>('complaintTypes', DEFAULT_COMPLAINT_TYPES);

    const setSubmitButtonLoading = useButtonLoadingState('[js-hook-message-form-submit]');

    useStylesheet(style);

    const contactReasons: ContactReason[] = ['Question', 'Complaint'];
    const contactReasonSelectOptions: SelectOption[] = [];
    const complaintTypeSelectOptions: SelectOption[] = [];
    let messageTranslation: string;
    const loadedAsComplaintForm = element.contactReason === 'Complaint';

    let form: BPDFormElement | null;
    let submitButton: HTMLElement | null;
    let complaintTypeSelectWrapper: HTMLDivElement | null;
    let complaintTypeSelect: HTMLSelectElement | null;

    onDidLoad(async () => {
        messageTranslation = await translateItem('Message');

        await generateTranslatedOptions(
            contactReasons,
            contactReasonSelectOptions,
            element.contactReason,
        );
        await generateTranslatedOptions(complaintTypes, complaintTypeSelectOptions);

        renderForm();
        queryElements();
        bindEvents();
        toggleComplaintTypeSelect();
        setPrivacyStatement();
    });

    watchContactReason(toggleComplaintTypeSelect);

    function renderForm() {
        element.innerHTML = '';
        element.append(
            <bpd-form>
                <form class="message-form__container">
                    <Row>
                        <Col cols={[12, , 8]}>
                            <RadioElement
                                label={useTranslate('Salutation')}
                                validationTypes="required"
                                horizontal
                                options={[
                                    {
                                        label: useTranslate('Acronym-Mister'),
                                        id: 'message-form-gender-dhr',
                                        name: 'gender',
                                        value: 'Mr',
                                    },
                                    {
                                        label: useTranslate('Acronym-Misses'),
                                        id: 'message-form-gender-ms',
                                        name: 'gender',
                                        value: 'Mrs',
                                    },
                                ]}
                            />
                        </Col>
                        <Col cols={isGermany() ? [12, , 6] : [12, , 5]}>
                            <InputElement
                                label={useTranslate('FirstName')}
                                id="message-form-first-name"
                                name="first_name"
                                validationTypes="required,text"
                            />
                        </Col>
                        {!isGermany() && (
                            <Col cols={[12, , 2]}>
                                <InputElement
                                    label={useTranslate('LastNamePrefix')}
                                    id="message-form-last-name-prefix"
                                    name="last_name_prefix"
                                    validationTypes="text"
                                />
                            </Col>
                        )}
                        <Col cols={isGermany() ? [12, , 6] : [12, , 5]}>
                            <InputElement
                                label={useTranslate('LastName')}
                                id="message-form-last-name"
                                name="last_name"
                                validationTypes="required,text"
                            />
                        </Col>
                        <Col cols={[12]}>
                            <InputElement
                                label={useTranslate('EmailAdres')}
                                id="message-form-email"
                                name="email"
                                validationTypes="required,email"
                            />
                        </Col>
                        <Col
                            cols={[12]}
                            {...(loadedAsComplaintForm && { extraClass: 'u-sr-only ' })}>
                            <SelectElement
                                label={useTranslate('SelectReason')}
                                id="message-form-contact-reason"
                                name="contact_reason"
                                validationTypes="required"
                                customSelect
                                options={[
                                    {
                                        label: '',
                                        value: '',
                                        default: true,
                                    },
                                    ...contactReasonSelectOptions,
                                ]}
                                onOptionSelect={({ rawValue }) => {
                                    setContactReason(rawValue as ContactReason);
                                }}
                            />
                        </Col>
                        <Col
                            cols={[12]}
                            extraClass="message-form__complaint-type-wrapper u-sr-only">
                            <SelectElement
                                label={useTranslate('SelectComplaintCategory')}
                                id="message-form-complaint-type"
                                name="complaint_type"
                                validationTypes="required"
                                customSelect
                                options={[
                                    {
                                        label: '',
                                        value: '',
                                        default: true,
                                    },
                                    ...complaintTypeSelectOptions,
                                ]}
                            />
                        </Col>
                        <Col cols={[12]}>
                            <TextareaElement
                                label={messageTranslation}
                                id="message-form-message"
                                name="message"
                                required
                            />
                        </Col>
                        <Col cols={[12, , 9]} extraClass="s-rich-text">
                            <p js-hook-message-form-privacy-statement></p>
                        </Col>
                        <Col cols={[12, , 3]} extraClass="u-flex u-flex-end message-form__submit">
                            <Button
                                label={useTranslate('Send')}
                                variant="primary"
                                width="full-width-mobile"
                                hook="message-form-submit"
                                type="submit"
                                hasLoading
                                disabled
                            />
                        </Col>
                    </Row>
                </form>
            </bpd-form>,
        );
    }

    function queryElements() {
        form = element.querySelector<BPDFormElement>('bpd-form');
        submitButton = element.querySelector<HTMLButtonElement>('[js-hook-message-form-submit]');
        complaintTypeSelectWrapper = element.querySelector('.message-form__complaint-type-wrapper');
        complaintTypeSelect = complaintTypeSelectWrapper?.querySelector('select') || null;
    }

    function toggleComplaintTypeSelect() {
        if (!complaintTypeSelectWrapper || !complaintTypeSelect) return;

        if (element.contactReason === 'Complaint') {
            complaintTypeSelectWrapper.classList.remove(SR_ONLY_CLASS);
            complaintTypeSelect.disabled = false;
        } else {
            complaintTypeSelectWrapper.classList.add(SR_ONLY_CLASS);
            complaintTypeSelect.disabled = true;
        }

        updateSubmitButtonState();
    }

    function bindEvents() {
        if (!form) return;
        form.addEventListener('change', updateSubmitButtonState);

        form.addEventListener('serializedForm', async (e: CustomEvent) => {
            setSubmitButtonLoading(true);
            const {
                email,
                message,
                gender,
                first_name: firstName,
                last_name: lastName,
                last_name_prefix: lastNamePrefix,
                contact_reason: contactReason,
                complaint_type: complaintType,
            } = e.detail;

            const data: AskAQuestionProps = {
                gender,
                firstName,
                lastName,
                email,
                contactReason,
                message,
                refererUrl: window.location.href,
                areaId: window.__ENVIRONMENT_DETAILS__.areaId,
                ...(lastNamePrefix && { lastNamePrefix }),
                ...(complaintType && { complaintType }),
            };

            try {
                await askAQuestion(data);

                if (onSuccess) onSuccess();

                form?.clearInputs();
                submitButton?.setAttribute('disabled', 'disabled');
            } catch (error) {
                if (onError) onError(error);
            } finally {
                setSubmitButtonLoading(false);
            }
        });
    }

    async function setPrivacyStatement() {
        const privacyStatement = element.querySelector('[js-hook-message-form-privacy-statement]');
        const privacyStatementHTML = await translateItem('PrivacyLinkContactForm');
        privacyStatement!.innerHTML = privacyStatementHTML;
    }

    function updateSubmitButtonState() {
        if (!form?.validate || !submitButton) return;

        if (form.validate(false).valid) {
            submitButton.removeAttribute('disabled');
        } else {
            submitButton.setAttribute('disabled', 'disabled');
        }
    }

    async function generateTranslatedOptions(
        source: string[],
        destination: SelectOption[],
        selected?: string | null,
    ) {
        for (const option of source) {
            const translated = await translateItem(option);

            destination.push({
                label: translated || option,
                value: translated || option,
                rawValue: option,
                ...(option === selected && { selected: true }),
            });
        }
    }
};

MessageForm.props = {
    onSuccess: {
        type: Function,
    },
    onError: {
        type: Function,
    },
    contactReason: {
        type: String,
        reflectToAttr: true,
    },
    complaintTypes: {
        type: Array,
    },
};

defineElement('message-form', MessageForm);
